import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { isNumber, isString } from 'lodash';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { getSingleLocationOptions } from 'src/clients/locations/Device Locations API/@tanstack/react-query.gen';
import { CslCmpDeviceLocationsApiContractsGeoJsonFeatureCollectionV1 } from 'src/clients/locations/Device Locations API/types.gen';
import { DetailsSkeleton } from 'src/common/components/DetailsSkeleton';
import { SectionGridItem } from 'src/common/components/SectionGrid/SectionGridItem';
import { SectionGridRow } from 'src/common/components/SectionGrid/SectionGridRow';
import { fetchSimInventoryEntry } from 'src/simInventory/SimInventoryApi/simInventoryApi';
import { simDetailsQueries } from 'src/simInventory/SimInventoryDetails/queries/simDetailsQueries';

type SimLocationDetailsInfoContentProps = {
  data: Awaited<ReturnType<typeof fetchSimInventoryEntry>>;
  locationData?: CslCmpDeviceLocationsApiContractsGeoJsonFeatureCollectionV1;
};

const SimLocationDetailsInfoContent: FC<SimLocationDetailsInfoContentProps> = (props) => {
  const { data, locationData } = props;

  const simLocationFeature = locationData?.features?.[0];
  const locationProperties = simLocationFeature?.properties;

  const { t } = useTranslation();

  const networkSectionTestId = 'locationDetails.fields.network';
  const sessionInformationSectionTestId = 'locationDetails.fields.session-information';
  const lastKnownLocationSectionTestId = 'locationDetails.fields.last-known-location';

  return (
    <Fragment>
      <SectionGridRow
        data-testid={networkSectionTestId}
        title={t('simInventory.network')}
        sx={{ borderTop: 0 }}
      >
        <SectionGridItem
          label={t('simInventory.mobileNetworkOperator')}
          testId={`${networkSectionTestId}.field.mobile-network-operator`}
          value={data.mobileNetworkOperator}
          customGridSizes={{
            xsSize: 'auto' as const,
            smSize: 'auto' as const,
            mdSize: 'auto' as const,
            lgSize: 'auto' as const,
            xlSize: 'auto' as const,
          }}
          forceDisplay
        />
      </SectionGridRow>
      <SectionGridRow
        data-testid={sessionInformationSectionTestId}
        title={t('simInventory.sessionInformation')}
      >
        <SectionGridItem
          label={t('simInventory.apn')}
          testId={`${sessionInformationSectionTestId}.field.apn`}
          value={data.apn}
          forceDisplay
        />
        <SectionGridItem
          label={t('rSimInventory.lastIpAddress')}
          testId={`${sessionInformationSectionTestId}.field.last-ip-address`}
          value={data.currentIpAddress}
          forceDisplay
        />
        <SectionGridItem
          label={t('simInventory.lastRat')}
          testId={`${sessionInformationSectionTestId}.field.last-rat`}
          value={data.ratType}
          forceDisplay
        />
      </SectionGridRow>
      {locationData && (
        <SectionGridRow
          data-testid={lastKnownLocationSectionTestId}
          title={t('simInventory.lastKnownLocation')}
        >
          <SectionGridItem
            label={t('simInventory.latitude')}
            testId={`${lastKnownLocationSectionTestId}.field.latitude`}
            value={
              isNumber(simLocationFeature?.geometry?.coordinates?.[1])
                ? simLocationFeature.geometry.coordinates[1].toLocaleString()
                : undefined
            }
            forceDisplay
          />
          <SectionGridItem
            label={t('simInventory.longitude')}
            testId={`${lastKnownLocationSectionTestId}.field.longitude`}
            value={
              isNumber(simLocationFeature?.geometry?.coordinates?.[0])
                ? simLocationFeature.geometry.coordinates[0].toLocaleString()
                : undefined
            }
            forceDisplay
          />
          <SectionGridItem
            label={t('simInventory.lastUpdated')}
            testId={`${lastKnownLocationSectionTestId}.field.last-updated`}
            value={
              isString(locationProperties?.eventTimestamp)
                ? locationProperties.eventTimestamp
                : undefined
            }
            forceDisplay
          />
          <SectionGridItem
            label={t('simInventory.accuracy')}
            testId={`${lastKnownLocationSectionTestId}.field.accuracy`}
            value={
              isNumber(locationProperties?.accuracy)
                ? locationProperties.accuracy.toString()
                : undefined
            }
            forceDisplay
          />
        </SectionGridRow>
      )}
    </Fragment>
  );
};

export const SimLocationDetailsInfo: FC<{ iccid: string }> = (props) => {
  const { iccid } = props;

  const { t } = useTranslation();

  const { data, status } = useQuery({
    queryKey: simDetailsQueries.detail({ iccid }),
    queryFn: async ({ signal }) => {
      return fetchSimInventoryEntry(iccid, undefined, signal);
    },
    meta: {
      isLocationsDetails: true,
    },
  });

  const simLocationDetailOptions = getSingleLocationOptions({
    query: {
      DeviceId: data?.imsi ?? '',
    },
  });

  const hasImsi = !!data?.imsi;

  const { data: locationData, status: locationStatus } = useQuery({
    ...simLocationDetailOptions,
    enabled: hasImsi,
  });

  if (status === 'pending' || (hasImsi && locationStatus === 'pending')) {
    return <DetailsSkeleton numberOfRepetitions={3} />;
  }

  if (status === 'error' || (hasImsi && locationStatus === 'error')) {
    return <Typography textAlign="center">{t('common.somethingWrong')}</Typography>;
  }

  return (
    <Box data-testid="locationDetails.fields.sim">
      <SimLocationDetailsInfoContent
        data={data}
        locationData={hasImsi ? locationData : undefined}
      />
    </Box>
  );
};
